import { liquidGlassRowShadowSx, liquidGlassSurfaceFillSx, useLiquidGlassPress } from '@focuslee/shared-mini-app';
import { Box, Stack } from '@mui/material';
import { Caption, Text } from '@telegram-apps/telegram-ui';
import { hapticFeedback } from '@tma.js/sdk';
import { type FC, type ReactNode } from 'react';
import { CommonIcon } from '../common/CommonIcon';

type ProfileActionRowProps = {
    title: string;
    subtitle?: string;
    value?: ReactNode;
    icon: ReactNode;
    onClick: () => void;
};

export const ProfileActionRow: FC<ProfileActionRowProps> = ({ title, subtitle, value, icon, onClick }) => {
    const {
        pressed,
        onPointerDown,
        onPointerMove,
        onPointerUp,
        onPointerCancel,
        onClick: onPressClick,
    } = useLiquidGlassPress({
        preventDefault: true,
        onActivate: () => {
            hapticFeedback.impactOccurred.ifAvailable('light');
            onClick();
        },
    });

    return (
        <Box sx={{ borderRadius: '12px', overflow: 'visible', ...liquidGlassRowShadowSx }}>
            <Box
                component="button"
                type="button"
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerCancel}
                onClick={onPressClick}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    width: '100%',
                    m: 0,
                    p: 1.5,
                    border: 0,
                    borderRadius: '12px',
                    textAlign: 'left',
                    font: 'inherit',
                    color: 'inherit',
                    cursor: 'pointer',
                    WebkitTapHighlightColor: 'transparent',
                    outline: 'none',
                    ...liquidGlassSurfaceFillSx,
                    ...(pressed && { bgcolor: 'color-mix(in srgb, var(--tg-theme-text-color) 6%, transparent)' }),
                    '&:focus-visible': { outline: '2px solid var(--tg-theme-link-color)', outlineOffset: 2 },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{icon}</Box>
                <Stack spacing={0} sx={{ flex: 1, minWidth: 0 }}>
                    <Text style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.2 }}>{title}</Text>
                    {subtitle && (
                        <Caption style={{ color: 'var(--tg-theme-hint-color)', fontSize: 11, lineHeight: 1.3 }}>{subtitle}</Caption>
                    )}
                </Stack>
                {value}
                <CommonIcon icon="keyboard_arrow_right" type="filled" size={24} color="var(--tg-theme-hint-color)" />
            </Box>
        </Box>
    );
};
